// Pure view-model extraction and diffing for methodology comparison. Input is the
// deserialized MethodologySet proto (camelCase property access), narrowed here to a
// loose shape so this module stays free of proto class-type friction.

import { computeSupersededIds, flattenRunGroups, getTotalCores } from '@aero-platform/shared'

interface RawNameValue {
  name?: string
  value?: string
}
interface RawHardware {
  slots?: number
  coresPerSlot?: number
  coreType?: string
  walltime?: number
}
interface RawSystem {
  name?: string
  vendorVersion?: string
  branchName?: string
  commitId?: string
  isHardwareOverride?: boolean
  hardwareOverrides?: RawHardware
}
interface RawStarConfig {
  stepOrder?: string[]
  starccmParameters?: { paramList?: RawNameValue[] }
  miscellaneousParameters?: { paramList?: RawNameValue[] }
}
export interface RawMethodology {
  _id?: string
  name?: string
  description?: string
  version?: string
  starccmConfig?: RawStarConfig
  selectedSystems?: RawSystem[]
  hardwareOverrides?: RawHardware
}

// Loose shape of a WorkflowSubmission (run) toObject(): a run embeds the methodology
// it ran under at runConfig.selectedMethodSet, which is the source for the diff.
export interface RawRunAttitudeState {
  name?: string
  frh?: number
  rrh?: number
  speed?: number
  yaw?: number
  steer?: number
  roll?: number
}
export interface RawRun {
  _id?: string
  name?: string
  projectName?: string
  overallStatus?: string
  runConfig?: {
    description?: string
    selectedMethodSet?: RawMethodology
    geometryChangeImage?: string[]
    selectedCases?: { name?: string; selectedAttitudeState?: RawRunAttitudeState }[]
    selectedAttitudeSet?: { name?: string; attitudeStates?: RawRunAttitudeState[] }
  }
  submissionRunGroups?: {
    name?: string
    workflowRunGroups?: { name?: string; systemRunId?: string }[]
  }[]
}

export const getRunMethodology = (run?: RawRun | null): RawMethodology | null =>
  run?.runConfig?.selectedMethodSet ?? null

// A run's per-case gm_ids come from each workflow run group's systemRunId ("0-83741"),
// where the gm_id is the numeric part after the submission-group prefix. These are the
// exact case ids to join against headline-data (robust vs. project/run name matching).
export const getRunCaseGmIds = (run?: RawRun | null): string[] => {
  const ids = new Set<string>()
  // Drop superseded (resubmitted) run groups so a case's old gm_id isn't collected alongside its
  // new one — otherwise the Results table/charts show duplicate rows mixing different SIM versions.
  const superseded = computeSupersededIds(flattenRunGroups(run))
  run?.submissionRunGroups?.forEach(sg =>
    sg.workflowRunGroups?.forEach(wrg => {
      const sys = wrg.systemRunId
      if (!sys || superseded.has(sys)) return
      const gm = String(sys).replace(/^\d+-/, '')
      if (/^\d+$/.test(gm)) ids.add(gm)
    })
  )
  return Array.from(ids)
}

export interface RunCaseRef {
  caseName: string
  /** Gallery key ("0-83741"), as used by PostProcGallery / the post-proc images API. */
  systemRunId: string
}

/** A run's cases (name + gallery systemRunId) for the post-proc image comparison. */
export const getRunCases = (run?: RawRun | null): RunCaseRef[] => {
  const out: RunCaseRef[] = []
  const seen = new Set<string>()
  // Exclude superseded (resubmitted) run groups so the comparison uses each case's active run only.
  const superseded = computeSupersededIds(flattenRunGroups(run))
  run?.submissionRunGroups?.forEach(sg =>
    sg.workflowRunGroups?.forEach(wrg => {
      const sys = wrg.systemRunId
      const name = wrg.name
      if (!sys || !name || superseded.has(sys) || seen.has(name)) return
      seen.add(name)
      out.push({ caseName: name, systemRunId: String(sys) })
    })
  )
  return out
}

export interface RunAttitudeRow extends RawRunAttitudeState {
  caseName: string
}

/** Flatten a run's selected cases into attitude rows (frh/rrh/speed per case). */
export const getRunAttitudes = (run?: RawRun | null): RunAttitudeRow[] =>
  (run?.runConfig?.selectedCases || []).map(c => ({
    caseName: c.name ?? '',
    ...(c.selectedAttitudeState || {})
  }))

export interface ConfigParam {
  name: string
  value: string
}
export interface SystemSummary {
  name: string
  vendorVersion: string
  branchName: string
  commitId: string
}
export interface HardwareSummary {
  slots: number
  coresPerSlot: number
  coreType: string
  walltime: number
  totalCores: number
}
export interface MethodologyViewModel {
  id: string
  name: string
  description: string
  version: string
  starParams: ConfigParam[]
  miscParams: ConfigParam[]
  stepOrder: string[]
  systems: SystemSummary[]
  hardware: HardwareSummary
}

const mapParams = (list?: RawNameValue[]): ConfigParam[] =>
  (list || []).map(p => ({ name: p.name ?? '', value: p.value ?? '' }))

export const toViewModel = (m: RawMethodology): MethodologyViewModel => {
  const star = m.starccmConfig
  const hw = m.hardwareOverrides || {}
  const slots = hw.slots ?? 0
  const coresPerSlot = hw.coresPerSlot ?? 0
  return {
    id: m._id ?? '',
    name: m.name ?? '',
    description: m.description ?? '',
    version: m.version ?? '',
    starParams: mapParams(star?.starccmParameters?.paramList),
    miscParams: mapParams(star?.miscellaneousParameters?.paramList),
    stepOrder: star?.stepOrder ?? [],
    systems: (m.selectedSystems || []).map(s => ({
      name: s.name ?? '',
      vendorVersion: s.vendorVersion ?? '',
      branchName: s.branchName ?? '',
      commitId: s.commitId ?? ''
    })),
    hardware: {
      slots,
      coresPerSlot,
      coreType: hw.coreType ?? '',
      walltime: hw.walltime ?? 0,
      totalCores: getTotalCores(hw)
    }
  }
}

export type DiffStatus = 'same' | 'changed' | 'added' | 'removed'

export interface DiffRow {
  name: string
  baseValue?: string
  propValue?: string
  status: DiffStatus
}

/** Diff two name/value lists into union rows, flagging added/removed/changed. */
export const diffParams = (base: ConfigParam[], prop: ConfigParam[]): DiffRow[] => {
  const baseMap = new Map(base.map(p => [p.name, p.value]))
  const propMap = new Map(prop.map(p => [p.name, p.value]))
  const names = Array.from(new Set([...baseMap.keys(), ...propMap.keys()])).sort()
  return names.map(name => {
    const inBase = baseMap.has(name)
    const inProp = propMap.has(name)
    const baseValue = baseMap.get(name)
    const propValue = propMap.get(name)
    let status: DiffStatus = 'same'
    if (inBase && !inProp) status = 'removed'
    else if (!inBase && inProp) status = 'added'
    else if (baseValue !== propValue) status = 'changed'
    return { name, baseValue, propValue, status }
  })
}

export interface StepDiff {
  step: string
  status: DiffStatus
}

/** Ordered union of step orders flagging steps only in one side or reordered between the two. */
export const diffSteps = (base: string[], prop: string[]): StepDiff[] => {
  const baseSet = new Set(base)
  const propSet = new Set(prop)
  // Position of each shared step among the steps common to both sides. A step present in both but
  // at a different common-order index has been reordered (e.g. [mesh, solve] vs [solve, mesh]) and
  // must read as 'changed', not 'same', so the macro-order change count is non-zero.
  const commonBaseOrder = new Map(base.filter(s => propSet.has(s)).map((s, i) => [s, i]))
  const commonPropOrder = new Map(prop.filter(s => baseSet.has(s)).map((s, i) => [s, i]))
  const ordered: string[] = []
  const seen = new Set<string>()
  ;[...prop, ...base].forEach(step => {
    if (!seen.has(step)) {
      seen.add(step)
      ordered.push(step)
    }
  })
  return ordered.map(step => {
    const inBase = baseSet.has(step)
    const inProp = propSet.has(step)
    let status: DiffStatus
    if (inBase && inProp) {
      status = commonBaseOrder.get(step) === commonPropOrder.get(step) ? 'same' : 'changed'
    } else {
      status = inProp ? 'added' : 'removed'
    }
    return { step, status }
  })
}

export const diffCount = (rows: { status: DiffStatus }[]): number =>
  rows.filter(r => r.status !== 'same').length
